<template>
  <v-container>
    <v-row>
      <!-- CALENDARIO + LISTA -->
      <v-col cols="12" class="py-0">
        <v-card class="k-surface-card" elevation="0">
          <!-- HEADER (Hoy + botón Evento) -->
          

          <!-- CALENDARIO -->
          <CalendarView
            :events="eventsForCalendar"
            :groups="groups"
            :uid="uid"
            @day-click="onDayClick"
            @event-click="onCalendarEventClick"
          />
          <div class="d-flex align-center my-3">
            <v-spacer />
            <v-btn color="primary" depressed @click="openCreate" class="rounded-l">
              <v-icon left>mdi-plus</v-icon>
              {{ $t("calendar.addEvent") }}
            </v-btn>
          </div>

          <!-- LISTA DE EVENTOS DEL DÍA -->
          <v-divider class="my-4" />

          <div ref="taskList" class="k-task-list-section">
            <div class="d-flex align-center mb-3">
              <div class="k-day-title">
                {{ selectedDateLabel }}
              </div>
              <v-spacer />
            </div>

            <div v-if="eventsForSelectedDate.length" class="k-events">
              <div
                v-for="e in eventsForSelectedDate"
                :key="e.id || `${toMs(e.startAt)}-${e.title || e.name}`"
                class="k-event-row"
              >
                <!-- Hora (ÚNICO sitio) -->
                <div class="k-event-time">
                  <div class="k-event-time-main">
                    {{ formatEventTime(e) }}
                  </div>
                </div>

                <!-- Card clickable para abrir details -->
                <v-card
                  class="k-event-card"
                  :class="eventCardClass(e)"
                  :style="eventCardStyle(e)"
                  elevation="0"
                  @click="openEventDetails(e)"
                >
                  <div class="k-event-card-inner">
                    <div class="k-event-head">
                      <div class="k-event-title">
                        {{ e.title || e.name }}
                      </div>

                      <!-- Resumen de acceso -->
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <div v-bind="attrs" v-on="on" class="k-access-icon">
                            <v-icon :color="accessSummaryColor(e)" small>
                              {{ accessSummaryIcon(e) }}
                            </v-icon>
                          </div>
                        </template>
                        <span>{{ accessSummaryLabel(e) }}</span>
                      </v-tooltip>

                      <!-- Editar SOLO si soy el creador -->
                      <v-tooltip bottom v-if="canDeleteEvent(e)">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            icon
                            small
                            class="ml-1"
                            color="primary"
                            @click.stop="onEditEvent(e)"
                          >
                            <v-icon small>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("calendar.editEvent") }}</span>
                      </v-tooltip>

                      <!-- Eliminar SOLO si soy el creador -->
                      <v-tooltip bottom v-if="canDeleteEvent(e)">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-bind="attrs"
                            v-on="on"
                            icon
                            small
                            class="ml-1"
                            color="error"
                            @click.stop="onDeleteEvent(e)"
                          >
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("calendar.deleteEvent") }}</span>
                      </v-tooltip>
                    </div>

                    <!-- Chips de grupos -->
                    <div class="k-event-chips">
                      <v-chip
                        v-for="gid in (e.groupIds || [])"
                        :key="`${e.id || toMs(e.startAt)}-${normalizeGroupId(gid)}`"
                        x-small
                        class="k-group-chip"
                        :outlined="accessForGroup(e, gid) === 'view'"
                        :color="chipColorForAccess(accessForGroup(e, gid))"
                        :text-color="accessForGroup(e, gid) === 'view' ? 'grey darken-3' : 'white'"
                      >
                        <v-icon left x-small>
                          {{ accessForGroup(e, gid) === "view" ? "mdi-eye" : "mdi-account-check" }}
                        </v-icon>
                        <span class="k-group-chip-mode">{{ accessLabelForGroup(e, gid) }}</span>
                        <span class="k-group-chip-separator">·</span>
                        <span class="k-group-chip-name">{{ groupLabelById(gid) }}</span>
                      </v-chip>
                    </div>
                  </div>
                </v-card>
              </div>
            </div>

            <v-alert v-else type="info" outlined class="k-empty">
              {{ $t("calendar.noEventsDay") }}
            </v-alert>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <EventDialog
      v-model="eventDialog"
      :groups="groups"
      :allowedGroupIds="allGroupIds"
      :initialDate="selectedDate"
      :event="eventToEdit"
      :mode="eventDialogMode"
      @create="createEvent"
      @update="updateEvent"
    />

    <EventDetailsDialog
      v-model="detailsDialog"
      :event="selectedEvent"
      :groups="groups"
      :uid="uid"
      :can-edit="canDeleteEvent(selectedEvent)"
      @edit="onEditEventFromDetails"
    />

    <v-dialog v-model="deleteEventDialog" max-width="420">
      <v-card>
        <v-card-text class="pt-6">
          {{ $t("calendar.confirmDeleteEvent") }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelDeleteEvent">{{ $t("common.no") }}</v-btn>
          <v-btn color="error" depressed @click="confirmDeleteEvent">{{ $t("common.yes") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import CalendarView from "../components/Calendar/CalendarView.vue";
import EventDetailsDialog from "../components/Events/EventDetailsDialog.vue";
import EventDialog from "../components/Events/EventDialog.vue";
import { deleteEvent } from "@/services/db";

function dateFromYMD(ymd) {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function toYMDLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function toDate(ts) {
  if (ts?.toDate) return ts.toDate();
  if (ts?.seconds) return new Date(ts.seconds * 1000);
  return new Date(ts);
}

export default {
  name: "CalendarPage",
  components: { CalendarView, EventDialog, EventDetailsDialog },

  data() {
    return {
      eventDialog: false,
      selectedDate: null,
      detailsDialog: false,
      selectedEvent: null,
      eventDialogMode: "create",
      eventToEdit: null,
      deleteEventDialog: false,
      eventToDelete: null
    };
  },

  computed: {
    ...mapGetters("events", ["events"]),
    ...mapGetters("groups", { groups: "groups" }),

    uid() {
      return this.$store.getters["auth/uid"];
    },

    groupById() {
      const map = {};
      (this.groups || []).forEach((g) => {
        const id = this.normalizeGroupId(g.id || g.groupId);
        if (id) map[id] = g;
      });
      return map;
    },

    allGroupIds() {
      return (this.groups || []).map((g) => g.id);
    },

    // Se ve todo (no filtramos aquí)
    eventsForCalendar() {
      return this.events || [];
    },

    selectedDateLabel() {
      if (!this.selectedDate) return this.$t("calendar.selectDay");
      return this.selectedDate.toLocaleDateString(this.$i18n.locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric"
      });
    },

    // Lista inferior: solo por fecha
    eventsForSelectedDate() {
      if (!this.selectedDate) return [];
      const targetYMD = toYMDLocal(this.selectedDate);

      return (this.events || [])
        .filter((e) => {
          if (!e.startAt) return false;
          const startDate = toDate(e.startAt);
          return toYMDLocal(startDate) === targetYMD;
        })
        .sort((a, b) => {
          const am = this.toMs(a.startAt);
          const bm = this.toMs(b.startAt);
          if (am !== bm) return am - bm;
          const at = (a.title || a.name || "").toLowerCase();
          const bt = (b.title || b.name || "").toLowerCase();
          return at.localeCompare(bt);
        });
    }
  },

  async created() {
    // mostrar contexto temporal inmediatamente
    if (!this.selectedDate) this.selectedDate = new Date();

    // cargar grupos y eventos en paralelo para reducir tiempo hasta primer pintado
    await Promise.allSettled([
      this.$store.dispatch("groups/refresh"),
      this.$store.dispatch("events/refresh")
    ]);
  },

  methods: {
    toMs(ts) {
      return toDate(ts).getTime();
    },

    canDeleteEvent(e) {
      return !!this.uid && !!e?.createdBy && e.createdBy === this.uid;
    },

    openEventDetails(e) {
      if (!e) return;
      this.selectedEvent = e;
      this.detailsDialog = true;
    },

    normalizeGroupId(gid) {
      if (!gid) return "";

      if (typeof gid === "string" || typeof gid === "number") return String(gid);

      if (gid.id) return String(gid.id);
      if (gid.groupId) return String(gid.groupId);

      if (gid.path && typeof gid.path === "string") {
        const parts = gid.path.split("/");
        return parts[parts.length - 1] || "";
      }

      return String(gid);
    },

    groupLabelById(gid) {
      const id = this.normalizeGroupId(gid);
      const g = this.groupById[id];
      return g?.name || g?.title || g?.label || this.$t("calendar.groupFallback", { id: id || "?" });
    },

    accessForGroup(e, gid) {
      const id = this.normalizeGroupId(gid);

      const share = e?.shareByGroup;
      if (share && id && share[id]) return share[id]; // "view" | "participate"

      const map = e?.accessByGroup || e?.groupAccess || e?.accessByGroupId;
      if (map && id && map[id]) return map[id];

      const a = e?.access?.access || e?.access;
      if (a === "view" || a === "participate") return a;

      return "participate";
    },

    chipColorForAccess(access) {
      return access === "view" ? "grey" : "primary";
    },

    accessLabelForGroup(e, gid) {
      return this.accessForGroup(e, gid) === "view"
        ? this.$t("calendar.viewing")
        : this.$t("calendar.participating");
    },

    accessModesForEvent(e) {
      const gids = e.groupIds || [];
      if (!gids.length) {
        const a = e?.access?.access || e?.access;
        return [a === "view" ? "view" : "participate"];
      }
      return gids.map((gid) => this.accessForGroup(e, gid));
    },

    accessSummary(e) {
      if (this.canDeleteEvent(e)) return "participate";

      const modes = this.accessModesForEvent(e);
      const hasParticipate = modes.includes("participate");
      const hasView = modes.includes("view");
      if (hasParticipate && hasView) return "mixed";
      return hasView ? "view" : "participate";
    },

    accessSummaryIcon(e) {
      const summary = this.accessSummary(e);
      if (summary === "mixed") return "mdi-account-eye";
      return summary === "view" ? "mdi-eye" : "mdi-account-check";
    },

    accessSummaryColor(e) {
      const summary = this.accessSummary(e);
      if (summary === "participate" && !this.canDeleteEvent(e)) return "secondary";
      if (summary === "mixed") return "secondary";
      return summary === "view" ? "grey" : "primary";
    },

    accessSummaryLabel(e) {
      const summary = this.accessSummary(e);
      if (summary === "mixed") return this.$t("calendar.mixedAccess");
      return summary === "view"
        ? this.$t("calendar.viewing")
        : this.$t("calendar.participating");
    },

    eventTone(e) {
      const summary = this.accessSummary(e);
      if (summary === "view") return "view";
      return this.canDeleteEvent(e) ? "mine" : "participate";
    },

    eventCardClass(e) {
      return `is-${this.eventTone(e)}`;
    },

    eventCardStyle(e) {
      const tone = this.eventTone(e);
      if (tone === "mine") {
        return {
          "--eventBorder": "#a57ce6",
          "--eventBg": "rgba(165, 124, 230, 0.08)",
          "--eventStripe": "#a57ce6"
        };
      }
      if (tone === "participate") {
        return {
          "--eventBorder": "rgba(167, 139, 250, 0.72)",
          "--eventBg": "rgba(167, 139, 250, 0.12)",
          "--eventStripe": "#c4b5fd"
        };
      }
      return {
        "--eventBorder": "rgba(0, 0, 0, 0.16)",
        "--eventBg": "rgba(0, 0, 0, 0.025)",
        "--eventStripe": "rgba(0, 0, 0, 0.24)"
      };
    },

    openCreate() {
      this.eventDialogMode = "create";
      this.eventToEdit = null;
      this.eventDialog = true;
    },

    onEditEvent(e) {
      if (!this.canDeleteEvent(e)) return;
      this.eventDialogMode = "edit";
      this.eventToEdit = e;
      this.eventDialog = true;
    },

    onEditEventFromDetails(e) {
      this.detailsDialog = false;
      this.onEditEvent(e);
    },

    onDayClick(date) {
      if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        this.selectedDate = dateFromYMD(date);
      } else {
        this.selectedDate = date instanceof Date ? date : new Date(date);
      }
      this.scrollToTaskList();
    },

    onCalendarEventClick({ ymd }) {
      this.onDayClick(ymd);
    },

    scrollToTaskList() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const el = this.$refs.taskList;
          if (!el) return;

          const scroller =
            el.closest(".v-main__wrap") ||
            el.closest(".v-main") ||
            document.scrollingElement ||
            document.documentElement;
          const scrollerRect = scroller.getBoundingClientRect
            ? scroller.getBoundingClientRect()
            : { top: 0, bottom: window.innerHeight };
          const topMargin = 16;
          const bottomMargin = this.$vuetify?.breakpoint?.smAndDown ? 88 : 16;
          const rect = el.getBoundingClientRect();
          const viewportHeight = scroller.clientHeight || scrollerRect.bottom - scrollerRect.top;
          const target =
            rect.height <= viewportHeight - topMargin - bottomMargin
              ? scroller.scrollTop + rect.bottom - scrollerRect.top - viewportHeight + bottomMargin
              : scroller.scrollTop + rect.top - scrollerRect.top - topMargin;

          scroller.scrollTo({
            top: Math.max(0, target),
            behavior: "smooth"
          });
        });
      });
    },

    async createEvent(payload) {
      await this.$store.dispatch("events/create", payload);
      await this.$store.dispatch("events/refresh");
    },

    async updateEvent(payload) {
      try {
        await this.$store.dispatch("events/update", payload);
        await this.$store.dispatch("events/refresh");

        if (this.selectedEvent?.id === payload.eventId) {
          this.selectedEvent =
            (this.events || []).find((e) => e.id === payload.eventId) || null;
        }
      } catch (err) {
        alert(err?.message || this.$t("calendar.updateEventError"));
      } finally {
        this.eventDialogMode = "create";
        this.eventToEdit = null;
      }
    },

    onDeleteEvent(e) {
      const id = e?.id;
      if (!id) {
        alert(this.$t("calendar.noEventId"));
        return;
      }

      this.eventToDelete = e;
      this.deleteEventDialog = true;
    },

    cancelDeleteEvent() {
      this.deleteEventDialog = false;
      this.eventToDelete = null;
    },

    async confirmDeleteEvent() {
      const id = this.eventToDelete?.id;
      if (!id) {
        this.cancelDeleteEvent();
        return;
      }

      try {
        await deleteEvent({ eventId: id, uid: this.uid });
        await this.$store.dispatch("events/refresh");

        // si estabas viendo el detalle de ese evento, cierra el dialog
        if (this.selectedEvent?.id === id) {
          this.detailsDialog = false;
          this.selectedEvent = null;
        }
      } catch (err) {
        alert(err?.message || this.$t("calendar.deleteEventError"));
      } finally {
        this.cancelDeleteEvent();
      }
    },

    formatEventTime(e) {
      if (!e.startAt) return "";
      const s = toDate(e.startAt);
      const en = e.endAt ? toDate(e.endAt) : null;

      const timeFmt = { hour: "2-digit", minute: "2-digit" };
      if (!en) return s.toLocaleTimeString(this.$i18n.locale, timeFmt);

      const isAllDay =
        s.getHours() === 0 &&
        s.getMinutes() === 0 &&
        en.getHours() === 23 &&
        (en.getMinutes() === 59 || en.getMinutes() === 55);

      if (isAllDay) return this.$t("calendar.allDay");

      return `${s.toLocaleTimeString(this.$i18n.locale, timeFmt)}-${en.toLocaleTimeString(
        this.$i18n.locale,
        timeFmt
      )}`;
    }
  }
};
</script>

<style scoped>
.v-chip.v-chip--outlined {
  background: rgba(0, 0, 0, 0.03);
}

/* Card contenedora (para no confundir con .k-event-card de cada evento) */
.k-surface-card {
  border-radius: 16px;
  padding: 2px;
}

/* Header "Hoy" */
.k-today-title {
  font-weight: 800;
  font-size: 1.05rem;
  line-height: 1.2;
}

/* --- Header día --- */
.k-day-title {
  font-weight: 600;
  font-size: 1.05rem;
  line-height: 1.2;
  text-transform: capitalize;
}

/* --- Lista --- */
.k-events {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Fila: hora + card */
.k-event-row {
  display: grid;
  grid-template-columns: 92px 1fr;
  gap: 12px;
  align-items: start;
}

.k-event-head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

/* Hora */
.k-event-time {
  padding-top: 10px;
  text-align: right;
}

.k-event-time-main {
  font-size: 0.95rem;
  line-height: 1.1;
}

.k-event-time-sub {
  margin-top: 4px;
  font-size: 0.72rem;
  opacity: 0.65;
}

/* Card evento (clicable) */
.k-event-card {
  cursor: pointer;
  border-radius: 16px;
  border: 1px solid var(--eventBorder, #e1e1e8);
  background: linear-gradient(
    90deg,
    var(--eventStripe, transparent) 0,
    var(--eventStripe, transparent) 3px,
    var(--eventBg, #ffffff) 3px,
    var(--eventBg, #ffffff) 100%
  );
  overflow: hidden;
  transition: transform 120ms ease, box-shadow 120ms ease;
}

.k-event-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.04);
}

.k-event-card-inner {
  padding: 12px 12px 10px;
}

/* Título + iconos */
.k-event-title {
  font-size: 1.02rem;
  line-height: 1.2;
  flex: 1;
  min-width: 0;
  word-break: break-word;
}

.k-access-icon {
  width: 30px;
  height: 30px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ececf2;
  background: #fbfbfd;
}

/* Chips grupos */
.k-event-chips {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.k-group-chip {
  border-radius: 999px;
  max-width: 100%;
}

.k-group-chip-mode {
  font-weight: 400;
}

.k-group-chip-separator {
  margin: 0 4px;
  opacity: 0.75;
}

.k-group-chip-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Empty */
.k-empty {
  border-radius: 14px;
}

/* --- Responsive móvil --- */
@media (max-width: 480px) {
  .k-event-row {
    grid-template-columns: 76px 1fr;
    gap: 10px;
  }

  .k-event-time-main {
    font-size: 0.88rem;
  }

  .k-event-card-inner {
    padding: 11px 11px 9px;
  }
}
</style>
